import { Runner } from '../../utils';
import type { ComponentClass } from '..';
import type { Entity } from '../Entity';
import type { Scene } from '../Scene';
import { ID32 } from './ID32';

export type NotQueryComponent = { operator: string; component: ComponentClass };
export type OrQueryComponent = {
  operator: string;
  components: ComponentClass[];
};
export type QueryComponent =
  | ComponentClass
  | NotQueryComponent
  | OrQueryComponent;
export type QueryKey = {
  name: string;
  componentID: ID32;
  notComponentID: ID32;
  orComponentIDs: ID32[];
};

export enum QueryEvents {
  MODIFIED = 'modified',
  ADDED = 'added',
  REMOVED = 'removed',
}

export class Query {
  /** entities that belong in the query */
  public readonly entities: Entity[];
  /** whether or not the query is reactive */
  public reactive = false;
  /** a key used to identify the query */
  public key: string;
  /** name of the query */
  public name: string;

  public readonly runners = {
    added: new Runner('addedToQuery', 2),
    removed: new Runner('removedFromQuery', 2),
    modified: new Runner('modifiedQuery', 5),
  };

  protected readonly componentID: ID32 = new ID32();
  protected readonly notComponentID: ID32 = new ID32();
  protected readonly orComponentIDs: ID32[] = [];

  constructor(world: Scene, key: QueryKey, name: string) {
    this.entities = [];
    this.key = key.name;
    this.componentID = key.componentID;
    this.notComponentID = key.notComponentID;
    this.orComponentIDs = key.orComponentIDs;
    this.name = name;

    // Fill the query with the existing entities
    for (let i = 0; i < world.allEntities.children.length; i++) {
      const entity = world.allEntities.children[i];

      if (this.match(entity.componentBitHash)) {
        this.entities.push(entity);
      }
    }
  }

  /** Destroys the query */
  public destroy() {
    this.runners.added.destroy();
    this.runners.removed.destroy();
    this.runners.modified.destroy();
    this.entities.length = 0;
  }

  /** Gets the first entity in the query. */
  public get first(): Entity {
    return this.entities[0];
  }

  /**
   * Loops through all entities in the query backwards
   * @param callbackfn - callback on interaction
   */
  public forEach<T extends Entity>(
    callbackfn: (value: T, index: number, array: T[]) => void,
  ): void {
    for (let e = this.entities.length - 1; e >= 0; --e) {
      const entity = this.entities[e] as T;

      callbackfn(entity, e, this.entities as T[]);
    }
  }

  /**
   * Add entity to this query
   * @param entity - entity to be added
   */
  public addEntity(entity: Entity): void {
    const index = this.entities.indexOf(entity);

    if (!~index) {
      entity.queries.push(this);
      this.entities.push(entity);
      this.runners.added.run(entity, this);
    }
  }

  /**
   * Remove entity from this query
   * @param entity - entity to be removed
   */
  public removeEntity(entity: Entity): void {
    let index = this.entities.indexOf(entity);

    if (~index) {
      this.entities.splice(index, 1);
      // TODO: remove the need for entities to have access to its queries
      index = entity.queries.indexOf(this);
      entity.queries.splice(index, 1);
      this.runners.removed.run(entity, this);
    }
  }

  public modifiedQuery(entity: Entity, Class: ComponentClass, data: any): void {
    this.runners.modified.run(
      entity,
      entity.c[Class.NAME as keyof Entity['c']],
      data,
      this,
      Class,
    );
  }

  /**
   * checks if an entity exists in this query
   * @param entity - entity to be checked
   */
  public hasEntity(entity: Entity): boolean {
    return !!~this.entities.indexOf(entity);
  }

  /**
   * Checks if an entity id matches the query id.
   * @param entityId - id of the entity to check
   */
  public match(entityId: ID32): boolean {
    const matchesComponents = this.componentID.matchIndex(entityId);
    const matchesNotComponents = this.notComponentID.matchAnyIndex(entityId);
    let matchesOrComponents = true;

    if (this.orComponentIDs.length > 0) {
      matchesOrComponents = this.orComponentIDs.every((ids) =>
        ids.matchAnyIndex(entityId),
      );
    }

    return matchesComponents && !matchesNotComponents && matchesOrComponents;
  }
}
